import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { LEAD_STATUSES, inr, fmtDate } from "@/utils";
import { Sparkles, Plus, Phone, Mail, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const TIER_COLORS = { hot: "bg-red-100 text-red-700 border-red-300", warm: "bg-amber-100 text-amber-700 border-amber-300", cold: "bg-blue-100 text-blue-700 border-blue-300" };

export default function Sales() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", source: "walk-in", budget: 0, notes: "" });
  const [scoringId, setScoringId] = useState(null);

  const { data: leads = [] } = useQuery({
    queryKey: ["leads", projectId],
    queryFn: async () => (await api.get("/leads", { params: projectId ? { project_id: projectId } : {} })).data,
  });

  const cols = useMemo(() => {
    const g = Object.fromEntries(LEAD_STATUSES.map((s) => [s.key, []]));
    for (const l of leads) if (g[l.status]) g[l.status].push(l);
    return g;
  }, [leads]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- LEAD_STATUSES is a module-level constant, stable across renders

  async function addLead() {
    if (!form.name || !projectId) return toast.error("Name and project required");
    try {
      await api.post("/leads", { ...form, project_id: projectId, budget: Number(form.budget) || 0 });
      toast.success("Lead added");
      qc.invalidateQueries({ queryKey: ["leads"] });
      setAddOpen(false);
      setForm({ name: "", phone: "", email: "", source: "walk-in", budget: 0, notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  async function moveLead(lead, newStatus) {
    try {
      await api.patch(`/leads/${lead.id}`, { status: newStatus });
      qc.invalidateQueries({ queryKey: ["leads"] });
    } catch { toast.error("Update failed"); }
  }

  async function scoreLead(lead) {
    setScoringId(lead.id);
    try {
      const { data } = await api.post(`/leads/${lead.id}/ai-score`);
      toast.success(`AI Score: ${data.score} (${data.tier})`);
      qc.invalidateQueries({ queryKey: ["leads"] });
    } catch { toast.error("AI scoring failed"); }
    finally { setScoringId(null); }
  }

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Sales · CRM</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Sales Pipeline</h1>
          <p className="text-sm text-zinc-500 mt-1">Lead → Site Visit → Quotation → Booking. Drag through the funnel with one click.</p>
        </div>
        <div className="flex gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-64" data-testid="sales-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-lead-btn"><Plus size={14} className="mr-1" /> Add Lead</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Add Lead</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Project</Label>
                  <Select value={projectId} onValueChange={setProjectId}>
                    <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="walk-in">Walk-in</SelectItem><SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="google_ads">Google Ads</SelectItem><SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem><SelectItem value="event">Event</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Budget (₹)</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={addLead} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-lead">Add lead</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {LEAD_STATUSES.map((s) => (
          <div key={s.key} className="kanban-col">
            <div className="mb-2 flex items-center justify-between px-1">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-500">{s.label}</div>
              <span className="text-[11px] text-zinc-400 font-mono">{cols[s.key]?.length || 0}</span>
            </div>
            <div className="bg-zinc-100 border border-zinc-200 rounded-md min-h-[60vh] p-2 space-y-2">
              {(cols[s.key] || []).map((l) => (
                <Card key={l.id} className="bg-white border-zinc-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`lead-card-${l.id}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-heading font-semibold text-sm truncate">{l.name}</div>
                        <div className="text-[11px] text-zinc-500 flex flex-wrap gap-2">
                          {l.phone && <span className="inline-flex items-center gap-1"><Phone size={10} />{l.phone}</span>}
                          {l.email && <span className="inline-flex items-center gap-1"><Mail size={10} />{l.email}</span>}
                        </div>
                      </div>
                      {l.score != null && (
                        <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border ${TIER_COLORS[l.score_tier] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>
                          {l.score}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-zinc-500 font-mono">
                      <span>{l.source}</span>·<span>{inr(l.budget)}</span>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Select value={l.status} onValueChange={(v) => moveLead(l, v)}>
                        <SelectTrigger className="h-7 text-[11px] flex-1" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{LEAD_STATUSES.map((x) => <SelectItem key={x.key} value={x.key} className="text-xs">{x.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" className="h-7 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => scoreLead(l)} disabled={scoringId === l.id} data-testid={`score-lead-${l.id}`}>
                        {scoringId === l.id ? "…" : <Sparkles size={12} />}
                      </Button>
                    </div>
                    <div className="text-[10px] text-zinc-400">Last activity {fmtDate(l.last_activity_at)}</div>
                  </CardContent>
                </Card>
              ))}
              {(!cols[s.key] || cols[s.key].length === 0) && (
                <div className="text-[11px] text-zinc-400 text-center py-4">
                  <UserIcon size={16} className="mx-auto opacity-40" />
                  <div className="mt-1">Empty</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
